import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import styles from './ClarificationQuestions.module.css';
import Footer from '../../../components/common/Footer/Footer';

const ClarificationQuestions = () => {
  const navigate = useNavigate();

  const handleCreateProject = () => {
    const token = localStorage.getItem('token');
    if (token) {
      navigate('/homeowner/create-comparison');
    } else {
      navigate('/login');
    }
  };

  const [activeFeature, setActiveFeature] = useState(null);



  const features = [
    {
      id: 'analysis',
      icon: 'fas fa-search-plus',
      title: 'Gap Analysis & Detection',
      description: 'Our AI analyzes contractor proposals against your project requirements, identifying missing information, vague details, and potential risks across 10+ evaluation dimensions.',
      benefits: [
        'Comprehensive proposal evaluation',
        'Missing information identification',
        'Risk and weakness detection',
        'Scoring-based gap analysis'
      ]
    },
    {
      id: 'personalized',
      icon: 'fas fa-user-cog',
      title: 'Personalized Question Generation',
      description: 'Questions are tailored to your experience level, communication preferences, and project priorities. First-time homeowners get beginner-friendly language, while experienced users get technical details.',
      benefits: [
        'Experience-level customization',
        'Priority-based question focus',
        'Communication style adaptation',
        'Professional language optimization'
      ]
    },
    {
      id: 'comprehensive',
      icon: 'fas fa-list-check',
      title: 'Comprehensive Coverage',
      description: 'Every identified issue gets addressed with dedicated questions. No gaps are left unasked - from missing permits and insurance details to unclear timelines and payment terms.',
      benefits: [
        'Complete issue coverage',
        'No detail overlooked',
        'Systematic question organization',
        'Professional contractor communication'
      ]
    },
    {
      id: 'delivery',
      icon: 'fas fa-file-pdf',
      title: 'Professional Delivery System',
      description: 'Questions are formatted into professional PDFs ready to send to contractors. Upload their responses, add custom questions, and manage all clarifications in one place.',
      benefits: [
        'PDF generation for contractors',
        'Response upload capability',
        'Custom question additions',
        'Centralized clarification management'
      ]
    }
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Proposal Analysis',
      description: 'AI evaluates contractor proposals against your project baseline, identifying gaps, weaknesses, and missing information.',
      icon: 'fas fa-microscope'
    },
    {
      step: '02',
      title: 'Question Generation',
      description: 'AI creates personalized clarification questions tailored to your preferences, experience level, and identified proposal gaps.',
      icon: 'fas fa-brain'
    },
    {
      step: '03',
      title: 'Professional Formatting',
      description: 'Questions are formatted into professional PDFs organized by contractor, ready for immediate delivery.',
      icon: 'fas fa-file-alt'
    },
    {
      step: '04',
      title: 'Response Management',
      description: 'Upload contractor responses, add custom questions, and manage all clarifications through your dashboard.',
      icon: 'fas fa-tasks'
    }
  ];

  const questionTypes = [
    {
      title: 'Missing Information Questions',
      description: 'Address gaps where essential project details are completely absent from proposals.',
      icon: 'fas fa-question-circle',
      examples: [
        'What specific permits will you obtain for this project?',
        'Can you provide your insurance certificate with coverage details?',
        'What is the exact timeline for project milestones?',
        'How will you handle debris removal and site cleanup?'
      ]
    },
    {
      title: 'Clarification Questions',
      description: 'Seek clarity on vague or ambiguous statements that need more detailed explanation.',
      icon: 'fas fa-comments',
      examples: [
        'You mentioned "high-quality materials" - can you specify brands and grades?',
        'What does "standard industry practices" include for this project?',
        'Can you break down the "miscellaneous costs" line item?',
        'What specific contingency plans do you have for weather delays?'
      ]
    },
    {
      title: 'Risk Mitigation Questions',
      description: 'Address potential risks and liability concerns identified in the proposal analysis.',
      icon: 'fas fa-shield-alt',
      examples: [
        'How will you protect our property during construction?',
        'What happens if you encounter unexpected structural issues?',
        'How do you handle change orders and cost increases?',
        'What safety measures will be implemented on site?'
      ]
    },
    {
      title: 'Quality Assurance Questions',
      description: 'Ensure quality standards and accountability measures are clearly defined.',
      icon: 'fas fa-award',
      examples: [
        'What quality control processes will you follow?',
        'How will progress be documented and communicated?',
        'What warranty do you provide on workmanship?',
        'Who will supervise the work on a daily basis?'
      ]
    }
  ];

  const personalizationFeatures = [
    {
      title: 'Experience Level Adaptation',
      description: 'Questions adjusted based on your construction experience',
      percentage: 100
    },
    {
      title: 'Priority-Based Focus',
      description: 'Emphasis on your stated project priorities (schedule, budget, quality)',
      percentage: 95
    },
    {
      title: 'Communication Style Matching',
      description: 'Language and tone adapted to your preferred communication style',
      percentage: 92
    },
    {
      title: 'Comprehensive Issue Coverage',
      description: 'Every identified gap and risk addressed with dedicated questions',
      percentage: 98
    }
  ];

  const stats = [
    { number: '10+', label: 'Evaluation Dimensions', icon: 'fas fa-chart-line' },
    { number: '100%', label: 'Issue Coverage', icon: 'fas fa-bullseye' },
    { number: 'PDF', label: 'Professional Format', icon: 'fas fa-file-pdf' },
    { number: '< 3 min', label: 'Generation Time', icon: 'fas fa-clock' }
  ];

  return (
    <div className={styles.pageContainer}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroBackground}>
          <div className={styles.heroShapes}>
            <div className={styles.shape}></div>
            <div className={styles.shape}></div>
            <div className={styles.shape}></div>
          </div>
        </div>
        <div className={styles.heroContent}>
          <div className={styles.heroText}>
            <h1>Clarification Questions</h1>
            <p className={styles.heroSubtitle}>
              AI-generated clarification questions that address every gap in contractor proposals
            </p>
            <p className={styles.heroDescription}>
              Our AI analyzes contractor proposals against your project requirements, then generates 
              personalized, professional questions to address missing information, unclear details, 
              and potential risks. Get the answers you need before making your decision.
            </p>

          </div>
          <div className={styles.heroVisual}>
            <div className={styles.questionPreview}>
              <div className={styles.previewHeader}>
                <div className={styles.previewTabs}>
                  <div className={styles.previewTab}>Questions</div>
                  <div className={styles.previewTab}>Responses</div>
                  <div className={styles.previewTab}>Analysis</div>
                </div>
              </div>
              <div className={styles.previewContent}>
                <div className={styles.questionSample}>
                  <div className={styles.questionItem}>
                    <div className={styles.questionNumber}>1</div>
                    <div className={styles.questionText}>
                      Can you provide specific insurance coverage details and certificate?
                    </div>
                  </div>
                  <div className={styles.questionItem}>
                    <div className={styles.questionNumber}>2</div>
                    <div className={styles.questionText}>
                      What are the exact timeline milestones for this project?
                    </div>
                  </div>
                  <div className={styles.questionItem}>
                    <div className={styles.questionNumber}>3</div>
                    <div className={styles.questionText}>
                      How will you handle permits and inspections?
                    </div>
                  </div>
                </div>
                <div className={styles.previewStatus}>
                  <div className={styles.statusItem}>
                    <i className="fas fa-check-circle"></i>
                    <span>12 questions generated</span>
                  </div>
                  <div className={styles.statusItem}>
                    <i className="fas fa-file-pdf"></i>
                    <span>PDF ready for contractor</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className={styles.processSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <h2>How Clarification Questions Work</h2>
            <p>Our AI-powered system ensures no important detail is left unasked</p>
          </div>
          <div className={styles.processGrid}>
            {processSteps.map((step, index) => (
              <div key={index} className={styles.processStep}>
                <div className={styles.stepNumber}>{step.step}</div>
                <div className={styles.stepIcon}>
                  <i className={step.icon}></i>
                </div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
                {index < processSteps.length - 1 && (
                  <div className={styles.stepConnector}>
                    <i className="fas fa-arrow-right"></i>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className={styles.featuresSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <h2>Intelligent Question Generation Features</h2>
            <p>Comprehensive AI analysis that leaves no stone unturned in contractor evaluation</p>
          </div>
          <div className={styles.featuresGrid}>
            {features.map((feature, index) => (
              <div 
                key={feature.id} 
                className={`${styles.featureCard} ${activeFeature === feature.id ? styles.active : ''}`}
                onMouseEnter={() => setActiveFeature(feature.id)}
                onMouseLeave={() => setActiveFeature(null)}
              >
                <div className={styles.featureIcon}>
                  <i className={feature.icon}></i>
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
                <div className={styles.featureBenefits}>
                  {feature.benefits.map((benefit, idx) => (
                    <div key={idx} className={styles.benefit}>
                      <i className="fas fa-check"></i>
                      <span>{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Question Types Section */}
      <section className={styles.questionTypesSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <h2>Types of Questions Generated</h2>
            <p>Our AI creates different types of questions based on what's missing or unclear in proposals</p>
          </div>
          <div className={styles.questionTypesGrid}>
            {questionTypes.map((type, index) => (
              <div key={index} className={styles.questionTypeCard}>
                <div className={styles.typeIcon}>
                  <i className={type.icon}></i>
                </div>
                <h3>{type.title}</h3>
                <p>{type.description}</p>
                <div className={styles.exampleQuestions}>
                  <h4>Example Questions:</h4>
                  <ul>
                    {type.examples.map((example, idx) => (
                      <li key={idx}>
                        <i className="fas fa-quote-left"></i>
                        <span>{example}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Personalization Section */}
      <section className={styles.personalizationSection}>
        <div className={styles.container}>
          <div className={styles.personalizationContent}>
            <div className={styles.personalizationText}>
              <h2>Personalized for Your Needs</h2>
              <p className={styles.personalizationSubtitle}>
                Questions are tailored to your experience level, priorities, and communication preferences
              </p>
              <div className={styles.personalizationList}>
                {personalizationFeatures.map((feature, index) => (
                  <div key={index} className={styles.personalizationItem}>
                    <div className={styles.personalizationHeader}>
                      <h4>{feature.title}</h4>
                      <span className={styles.percentage}>{feature.percentage}%</span>
                    </div>
                    <p>{feature.description}</p>
                    <div className={styles.personalizationBar}>
                      <div 
                        className={styles.personalizationProgress} 
                        style={{ width: `${feature.percentage}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className={styles.personalizationVisual}>
              <div className={styles.personalizationCard}>
                <h4>Personalization Examples</h4>
                <div className={styles.exampleItem}>
                  <div className={styles.userType}>
                    <i className="fas fa-user-graduate"></i>
                    <span>First-time Homeowner</span>
                  </div>
                  <div className={styles.exampleQuestion}>
                    "Can you explain in simple terms what permits you'll need and how long they typically take?"
                  </div>
                </div>
                <div className={styles.exampleItem}>
                  <div className={styles.userType}>
                    <i className="fas fa-user-tie"></i>
                    <span>Experienced Homeowner</span>
                  </div>
                  <div className={styles.exampleQuestion}>
                    "Provide permit schedule with dependencies and inspection sequence for this scope."
                  </div>
                </div>
                <div className={styles.exampleItem}>
                  <div className={styles.userType}>
                    <i className="fas fa-clock"></i>
                    <span>Schedule Priority</span>
                  </div>
                  <div className={styles.exampleQuestion}>
                    "What specific timeline contingencies do you have for weather delays and material delivery?"
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className={styles.statsSection}>
        <div className={styles.container}>
          <div className={styles.statsContent}>
            <div className={styles.statsText}>
              <h2>Clarification Questions by the Numbers</h2>
              <p>Comprehensive coverage that ensures nothing important is missed</p>
            </div>
            <div className={styles.statsGrid}>
              {stats.map((stat, index) => (
                <div key={index} className={styles.statCard}>
                  <div className={styles.statIcon}>
                    <i className={stat.icon}></i>
                  </div>
                  <div className={styles.statContent}>
                    <span className={styles.statNumber}>{stat.number}</span>
                    <span className={styles.statLabel}>{stat.label}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className={styles.benefitsSection}>
        <div className={styles.container}>
          <div className={styles.benefitsContent}>
            <div className={styles.benefitsText}>
              <h2>Why Clarification Questions Matter</h2>
              <div className={styles.benefitsList}>
                <div className={styles.benefitItem}>
                  <i className="fas fa-shield-check"></i>
                  <div>
                    <h4>Avoid Surprises</h4>
                    <p>Get clear answers upfront to prevent costly misunderstandings during construction</p>
                  </div>
                </div>
                <div className={styles.benefitItem}>
                  <i className="fas fa-handshake"></i>
                  <div>
                    <h4>Better Communication</h4>
                    <p>Establish clear communication channels and expectations with contractors</p>
                  </div>
                </div>
                <div className={styles.benefitItem}>
                  <i className="fas fa-balance-scale"></i>
                  <div>
                    <h4>Fair Comparison</h4>
                    <p>Compare proposals accurately when all contractors provide the same level of detail</p>
                  </div>
                </div>
                <div className={styles.benefitItem}>
                  <i className="fas fa-user-shield"></i>
                  <div>
                    <h4>Risk Protection</h4>
                    <p>Identify and address potential risks before they become expensive problems</p>
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.benefitsVisual}>
              <div className={styles.benefitsCard}>
                <h4>What Gets Clarified</h4>
                <div className={styles.clarificationList}>
                  <div className={styles.clarificationItem}>
                    <i className="fas fa-file-contract"></i>
                    <span>Missing insurance & permits</span>
                  </div>
                  <div className={styles.clarificationItem}>
                    <i className="fas fa-calendar-alt"></i>
                    <span>Unclear timelines & milestones</span>
                  </div>
                  <div className={styles.clarificationItem}>
                    <i className="fas fa-dollar-sign"></i>
                    <span>Vague cost breakdowns</span>
                  </div>
                  <div className={styles.clarificationItem}>
                    <i className="fas fa-tools"></i>
                    <span>Unspecified materials & methods</span>
                  </div>
                  <div className={styles.clarificationItem}>
                    <i className="fas fa-exclamation-triangle"></i>
                    <span>Potential risks & liabilities</span>
                  </div>
                  <div className={styles.clarificationItem}>
                    <i className="fas fa-phone"></i>
                    <span>Communication preferences</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={styles.ctaSection}>
        <div className={styles.container}>
          <div className={styles.ctaContent}>
            <h2>Get the Answers You Need</h2>
            <p>Generate comprehensive clarification questions tailored to your project and preferences</p>
            <div className={styles.ctaButtons}>
              <button className={styles.primaryButton} onClick={handleCreateProject}>
                <i className="fas fa-rocket"></i>
                Start Question Generation
              </button>
              <button className={styles.secondaryButton} onClick={() => navigate('/homeowner/pricing')}>
                <i className="fas fa-tag"></i>
                View Pricing
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ClarificationQuestions; 